import { useMemo, useRef, useState, useLayoutEffect } from "react";
import { formatTomanInt } from "../utils/format";
import type { DashboardUsageData } from "./StackedAreaChart";

interface KeySpendBarChartProps {
  data?: DashboardUsageData;
  loading?: boolean;
}

const OTHERS = "others";
const OTHERS_LABEL = "سایر";
// Same palette as the /dashboard radial bar chart
const PALETTE = ["#22C55E", "#60A5FA", "#A78BFA", "#FB923C"];
const OTHERS_COLOR = "#94A3B8";
const BAR_HEIGHT = 168;

function formatDayLabel(iso: string, i: number, total: number): string {
  const d = new Date(iso);
  const day = d.toLocaleString("fa-IR", { day: "numeric", month: "short" });
  if (total <= 8) return day;
  // Show a label every 5th bucket to keep it readable.
  if (i % 5 === 0) return day;
  return "";
}

function niceMax(max: number): number {
  if (max <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(max)));
  const frac = max / pow;
  let nice = 10;
  if (frac <= 1) nice = 1;
  else if (frac <= 2) nice = 2;
  else if (frac <= 2.5) nice = 2.5;
  else if (frac <= 5) nice = 5;
  return nice * pow;
}

export default function KeySpendBarChart({ data, loading }: KeySpendBarChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const [mouseX, setMouseX] = useState<number | null>(null);
  const [mouseY, setMouseY] = useState<number | null>(null);
  const [tooltipW, setTooltipW] = useState(0);
  const [tooltipH, setTooltipH] = useState(0);

  const buckets = data?.buckets || [];
  const baseSeries = useMemo(() => [...(data?.models || [])], [data]);
  const colorFor = useMemo(() => {
    const map = new Map<string, string>();
    baseSeries.forEach((s, i) => {
      if (s === OTHERS) map.set(s, OTHERS_COLOR);
      else map.set(s, PALETTE[i % PALETTE.length]);
    });
    return map;
  }, [baseSeries]);

  const { totals, maxValue, grandTotal } = useMemo(() => {
    const totals = buckets.map((b) =>
      baseSeries.reduce((sum, s) => sum + (Number(b.values[s]) || 0), 0)
    );
    const grandTotal = totals.reduce((a, b) => a + b, 0);
    return { totals, maxValue: niceMax(Math.max(0, ...totals) / 10), grandTotal };
  }, [buckets, baseSeries]);

  useLayoutEffect(() => {
    if (tooltipRef.current) {
      setTooltipW(tooltipRef.current.offsetWidth);
      setTooltipH(tooltipRef.current.offsetHeight);
    }
  }, [hovered]);

  if (loading) {
    return (
      <div style={{ height: BAR_HEIGHT + 40 }}>
        <div className="h-40 w-full bg-surface-hover rounded animate-pulse" />
      </div>
    );
  }

  if (!buckets.length || grandTotal <= 0) {
    return (
      <div
        style={{ height: BAR_HEIGHT + 40 }}
        className="flex items-center justify-center text-text-tertiary"
      >
        <p className="text-body">داده‌ای برای نمایش وجود ندارد</p>
      </div>
    );
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = chartRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const relX = e.clientX - rect.left;
    const relY = e.clientY - rect.top;
    const idx = Math.min(
      Math.max(Math.floor((relX / rect.width) * buckets.length), 0),
      buckets.length - 1
    );
    setHovered(idx);
    setMouseX(relX);
    setMouseY(relY);
  };

  const ticks = [0, 1, 2, 3, 4].map((i) => (maxValue * i) / 4);

  const hoveredEntries =
    hovered != null
      ? baseSeries
          .map((s) => ({
            name: s,
            color: colorFor.get(s) || "#999",
            irr: Number(buckets[hovered].values[s]) || 0,
          }))
          .filter((e) => e.irr > 0)
      : [];
  const hoveredTotal = hoveredEntries.reduce((a, e) => a + e.irr, 0);

  // Tooltip on whichever side of the mouse has more room.
  const GAP = 12;
  const EDGE = 6;
  let tooltipLeft = 0;
  let tooltipTop = 0;
  if (chartRef.current && mouseX != null && mouseY != null) {
    const width = chartRef.current.clientWidth;
    const height = chartRef.current.clientHeight;
    const spaceLeft = mouseX - GAP;
    const spaceRight = width - mouseX - GAP;
    tooltipLeft =
      spaceRight >= spaceLeft ? mouseX + GAP : mouseX - GAP - tooltipW;
    tooltipLeft = Math.min(Math.max(tooltipLeft, EDGE), width - tooltipW - EDGE);
    const rawTop = mouseY - tooltipH / 2;
    tooltipTop = Math.min(Math.max(rawTop, EDGE), height - tooltipH - EDGE);
  }

  return (
    <div>
      <div
        ref={chartRef}
        dir="ltr"
        className="relative select-none"
        style={{ height: BAR_HEIGHT }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHovered(null)}
      >
        {/* Y axis ticks */}
        <div className="absolute left-0 top-0 bottom-0 w-9">
          {ticks.map((t) => (
            <span
              key={t}
              dir="rtl"
              className="absolute right-0 -translate-y-1/2 text-[9px] text-text-tertiary"
              style={{ top: `${(1 - t / maxValue) * 100}%` }}
            >
              {t.toLocaleString("fa-IR", { maximumFractionDigits: 0 })}
            </span>
          ))}
        </div>

        <div className="absolute left-9 right-0 top-0 bottom-0">
          {ticks.map((t) => (
            <div
              key={t}
              className="absolute left-0 right-0"
              style={{ top: `${(1 - t / maxValue) * 100}%`, height: 1, background: "rgb(var(--color-border))" }}
            />
          ))}

          {/* Bars */}
          <div className="absolute inset-0 flex items-end gap-px">
            {buckets.map((b, i) => {
              const toman = totals[i] / 10;
              const pct = maxValue > 0 ? (toman / maxValue) * 100 : 0;
              return (
                <div
                  key={i}
                  className="relative flex-1 h-full flex flex-col justify-end rounded-t"
                  style={{ minWidth: 2 }}
                >
                  {baseSeries.map((s) => {
                    const irr = Number(b.values[s]) || 0;
                    const h = maxValue > 0 ? (irr / 10 / maxValue) * 100 : 0;
                    if (h <= 0) return null;
                    return (
                      <div
                        key={s}
                        className="w-full"
                        style={{
                          height: `${h}%`,
                          background: colorFor.get(s),
                        }}
                      />
                    );
                  })}
                  {pct === 0 && (
                    <div className="w-full h-0.5 rounded bg-track" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Tooltip — sibling of the plot area so left/top match the mouse coords */}
        {hovered != null && hoveredEntries.length > 0 && (
          <div className="absolute z-30 pointer-events-none" style={{ left: tooltipLeft, top: tooltipTop }}>
            <div
              ref={tooltipRef}
              dir="rtl"
              className="px-2.5 py-2 rounded-btn whitespace-nowrap"
              style={{
                background: "rgba(26,26,26,0.92)",
                color: "#fff",
                boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
              }}
            >
              <p className="text-[10px] mb-1" style={{ opacity: 0.8 }}>
                {new Date(buckets[hovered].time).toLocaleDateString("fa-IR", {
                  day: "numeric",
                  month: "long",
                })}
              </p>
              {hoveredEntries.map((e) => (
                <div key={e.name} className="flex items-center justify-between gap-4 py-0.5">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: e.color }} />
                    <span dir="ltr" className="text-[10px] truncate max-w-[130px]">
                      {e.name === OTHERS ? OTHERS_LABEL : e.name}
                    </span>
                  </div>
                  <span className="text-[11px] font-medium">
                    {formatTomanInt(e.irr)}
                  </span>
                </div>
              ))}
              <div className="my-1 h-px" style={{ background: "rgba(255,255,255,0.15)" }} />
              <div className="flex items-center justify-between gap-4">
                <span className="text-[10px]" style={{ opacity: 0.8 }}>مجموع</span>
                <span className="text-[11px] font-bold">{formatTomanInt(hoveredTotal)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* X labels */}
      <div className="flex gap-px mt-1 pr-9" dir="ltr">
        {buckets.map((b, i) => (
          <div key={i} className="flex-1 text-center overflow-hidden">
            <span
              dir="rtl"
              className="inline-block text-[9px] text-text-tertiary truncate"
            >
              {formatDayLabel(b.time, i, buckets.length)}
            </span>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3">
        {baseSeries.map((s) => (
          <div key={s} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: colorFor.get(s) }} />
            <span dir="ltr" className="text-[10px] text-text-main truncate max-w-[110px]">
              {s === OTHERS ? OTHERS_LABEL : s}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
